import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  rawValue?: number; // Raw numeric value for accurate comparison calculations
  icon: LucideIcon;
  iconColor?: string;
  comparison?: {
    value: number;
    label: string;
  };
  subtitle?: string;
}

function MetricCard({
  title,
  value,
  rawValue,
  icon: Icon,
  iconColor = 'text-primary',
  comparison,
  subtitle,
}: MetricCardProps) {
  const getChangePercent = () => {
    if (!comparison || comparison.value === 0) return 0;
    // Use rawValue if provided, otherwise try to parse the display value
    const currentValue = rawValue !== undefined
      ? rawValue
      : (typeof value === 'number' ? value : parseFloat(value.toString().replace(/[^0-9.-]/g, '')));
    if (isNaN(currentValue)) return 0;
    return ((currentValue - comparison.value) / comparison.value) * 100;
  };

  const changePercent = comparison ? getChangePercent() : null;

  const getTrendIcon = () => {
    if (changePercent === null) return null;
    if (changePercent > 0) return <TrendingUp className="h-3 w-3" />;
    if (changePercent < 0) return <TrendingDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  const getTrendColor = () => {
    if (changePercent === null) return '';
    if (changePercent > 0) return 'text-green-600 dark:text-green-400';
    if (changePercent < 0) return 'text-red-600 dark:text-red-400';
    return 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg border p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground truncate">{title}</p>
          <p className="text-2xl font-bold mt-1 truncate">{value}</p>
          {comparison && changePercent !== null && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${getTrendColor()}`}>
              {getTrendIcon()}
              <span>
                {changePercent > 0 ? '+' : ''}
                {changePercent.toFixed(1)}% {comparison.label}
              </span>
            </div>
          )}
          {subtitle && !comparison && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg bg-secondary/50 ${iconColor}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
}

export default MetricCard;
